<template>
  <v-container class="workflow-section">
    <div class="section-header text-center mb-12">
      <h2 class="section-title">How It Works</h2>
      <p class="section-subtitle">Simple 3-step process to automate your purchase orders</p>
    </div>
    
    <v-row class="workflow-container" justify="center" align="center">
      <v-col
        v-for="(step, index) in workflowSteps"
        :key="step.id"
        cols="12"
        sm="6"
        md="4"
        class="workflow-col"
      >
        <workflow-step
          :step="index + 1"
          :title="step.title"
          :description="step.description"
          :icon="step.icon"
          :route="step.route"
          :is-active="activeStep === index"
          @mouseenter="hoveredStep = index"
          @mouseleave="hoveredStep = null"
          @click="navigateToStep(step.route)"
        />
        
        <!-- Animated connector -->
        <div v-if="index < workflowSteps.length - 1" class="step-connector d-none d-md-block">
          <svg width="80" height="40" viewBox="0 0 80 40">
            <defs>
              <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style="stop-color:#667eea;stop-opacity:0.3" />
                <stop offset="100%" style="stop-color:#764ba2;stop-opacity:1" />
              </linearGradient>
            </defs>
            <path 
              d="M 10 20 L 60 20 L 55 15 M 60 20 L 55 25" 
              stroke="url(#arrowGradient)" 
              stroke-width="3" 
              fill="none"
              class="connector-arrow"
              :class="{ 'active': activeStep >= index }"
            />
          </svg>
        </div>
      </v-col>
    </v-row>
    
    <!-- Active step detail -->
    <v-row justify="center" class="mt-12">
      <v-col cols="12" md="8">
        <v-expand-transition>
          <v-card 
            v-if="activeStep !== null"
            class="step-detail-card"
            elevation="12"
          >
            <v-card-text class="pa-8 text-center">
              <v-icon 
                :color="workflowSteps[activeStep].color" 
                size="64"
                class="mb-4"
              >
                {{ workflowSteps[activeStep].icon }}
              </v-icon>
              <h3 class="text-h4 mb-4">{{ workflowSteps[activeStep].title }}</h3>
              <p class="text-h6 font-weight-light">{{ workflowSteps[activeStep].detailedDescription }}</p>
              <v-btn
                class="mt-6"
                color="primary"
                @click="navigateToStep(workflowSteps[activeStep].route)"
              >
                Go to {{ workflowSteps[activeStep].title }}
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-text>
          </v-card>
        </v-expand-transition>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import WorkflowStep from './WorkflowStep.vue';

export default {
  name: 'WorkflowDiagram',
  
  components: {
    WorkflowStep
  },
  
  data() {
    return {
      hoveredStep: null,
      activeStep: 0,
      workflowSteps: [
        {
          id: 'configure',
          title: 'Configure',
          description: 'Set up automation rules & schedule',
          detailedDescription: 'Define which products to order, set reorder points, choose suppliers, and configure your preferred ordering schedule (daily, weekly, or monthly).',
          icon: 'mdi-cog-outline',
          color: 'primary',
          route: '/automation-configs'
        },
        {
          id: 'review',
          title: 'Review',
          description: 'Check generated orders',
          detailedDescription: 'Review automatically generated purchase orders before they are sent. Make adjustments if needed, or approve with one click.',
          icon: 'mdi-clipboard-check-outline',
          color: 'success',
          route: '/purchase-orders/unapproved'
        },
        {
          id: 'track',
          title: 'Track',
          description: 'Monitor performance',
          detailedDescription: 'View real-time dashboards showing time saved, order accuracy, and automation performance metrics.',
          icon: 'mdi-chart-line',
          color: 'info',
          route: '/automation-dashboard'
        }
      ]
    };
  },
  
  mounted() {
    this.startStepRotation();
  },
  
  beforeDestroy() {
    if (this.rotationInterval) {
      clearInterval(this.rotationInterval);
    }
  },
  
  methods: {
    navigateToStep(route) {
      this.$router.push(route);
    },
    
    startStepRotation() {
      this.rotationInterval = setInterval(() => {
        this.activeStep = (this.activeStep + 1) % this.workflowSteps.length;
      }, 4000);
    }
  }
};
</script>

<style lang="scss" scoped>
.workflow-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 80px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(102, 126, 234, 0.08) 0%, transparent 70%);
    animation: rotate 30s linear infinite;
  }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.section-header {
  position: relative;
  z-index: 1;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 16px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.1);
}

.section-subtitle {
  font-size: 1.2rem;
  color: #546e7a;
  font-weight: 300;
}

.workflow-container {
  position: relative;
  z-index: 1;
}

.workflow-col {
  position: relative;
}

.step-connector {
  position: absolute;
  top: 50%;
  right: -40px;
  transform: translateY(-50%);
  z-index: 0;
}

.connector-arrow {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: drawArrow 2s ease-in-out infinite;
  
  &.active {
    stroke-dashoffset: 0;
  }
}

@keyframes drawArrow {
  0% {
    stroke-dashoffset: 100;
    opacity: 0.3;
  }
  50% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  100% {
    stroke-dashoffset: -100;
    opacity: 0.3;
  }
}

.step-detail-card {
  background: white;
  border-radius: 20px;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
  }
}

@media (max-width: 960px) {
  .workflow-section {
    padding: 60px 0;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .step-connector {
    display: none;
  }
}
</style> 